import { Suspense, useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Providers } from "@/src/components/Providers";
import { CheckoutPage } from "@/src/CheckoutPage";
import { ThankYouPage } from "@/src/ThankyouPage";

function RedirectComponent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Preserve query parameters during redirect
    const queryString = searchParams.toString();
    const redirectUrl = queryString ? `/checkout?${queryString}` : "/checkout";

    console.log("Redirecting from / to:", redirectUrl);
    navigate(redirectUrl, { replace: true });
  }, [navigate, searchParams]);

  // Show loading while redirecting
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
        <p className="mt-2 text-gray-600">Redirecting to checkout...</p>
      </div>
    </div>
  );
}

function HomePage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        </div>
      }
    >
      <RedirectComponent />
    </Suspense>
  );
}

function CheckoutContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutToken = searchParams.get("checkoutToken") || "";

  // Monitor URL changes (including programmatic updates) and sync with React Router
  useEffect(() => {
    const checkAndSyncToken = () => {
      const currentToken = new URLSearchParams(window.location.search).get(
        "checkoutToken",
      );
      const routerToken = searchParams.get("checkoutToken");

      // If URL has token but React Router doesn't, sync it
      if (currentToken && currentToken !== routerToken) {
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("checkoutToken", currentToken);
            return newParams;
          },
          { replace: true },
        );
      }
    };

    // Check immediately
    checkAndSyncToken();

    // Listen to popstate events (back/forward navigation)
    window.addEventListener("popstate", checkAndSyncToken);

    // Poll for URL changes (in case URL is updated programmatically)
    const intervalId = setInterval(checkAndSyncToken, 500);

    return () => {
      window.removeEventListener("popstate", checkAndSyncToken);
      clearInterval(intervalId);
    };
  }, [searchParams, setSearchParams]);

  // Log whenever checkoutToken changes
  useEffect(() => {
    console.log("[CheckoutContent] checkoutToken changed:", checkoutToken);
  }, [checkoutToken]);

  // Pass the checkout token to the component
  // The component will handle initialization via the init() function
  // Using key prop to force remount when checkoutToken changes
  return <CheckoutPage key={checkoutToken} checkoutToken={checkoutToken} />;
}

function CheckoutRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="mx-auto max-w-md">
            <div className="rounded-lg bg-white p-6 shadow">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
              <p className="mt-2 text-center text-sm text-gray-600">
                Loading...
              </p>
            </div>
          </div>
        </div>
      }
    >
      <CheckoutContent />
    </Suspense>
  );
}

function ThankYouContent() {
  const { orderId } = useParams<{ orderId: string }>();

  if (!orderId) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-red-600">
            Order ID Required
          </h1>
          <p className="mt-2 text-gray-600">No order ID provided in the URL.</p>
        </div>
      </div>
    );
  }

  return <ThankYouPage orderId={orderId} />;
}

function ThankYouRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="mx-auto max-w-md">
            <div className="rounded-lg bg-white p-6 shadow">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
              <p className="mt-2 text-center text-sm text-gray-600">
                Loading order details...
              </p>
            </div>
          </div>
        </div>
      }
    >
      <ThankYouContent />
    </Suspense>
  );
}

function App() {
  return (
    <Providers>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/checkout" element={<CheckoutRoute />} />
        <Route path="/thankyou/:orderId" element={<ThankYouRoute />} />
        {/* Catch all route - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Providers>
  );
}

export default App;
